
import { Component, Prop, Vue } from 'vue-property-decorator';
import * as StorageServices from '@/services/StorageServices';
import { ROOT_ACTIONS } from '@/store/actions';
import { mediaUrl } from '@/content/pages';

export type MediaKind = 'image' | 'video' | 'audio' | 'file';

const UPLOAD_LABELS: Record<MediaKind, string> = {
  image: 'Nieuwe afbeelding uploaden',
  video: 'Nieuwe video uploaden',
  audio: 'Nieuw geluidsfragment uploaden',
  file: 'Nieuw bestand uploaden (pdf, Word of PowerPoint)',
};

/**
 * Formats the website's image optimizer can read and every browser can show. Not `image/*`: that lets an iPhone photo
 * (HEIC) through, which the optimizer cannot convert and most browsers cannot display.
 */
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];

const ACCEPT: Record<MediaKind, string> = {
  image: IMAGE_TYPES.join(','),
  video: 'video/mp4',
  audio: 'audio/*',
  file: '.pdf,.doc,.docx,.ppt,.pptx,application/pdf',
};

/** "Checklist Fake News (2).pdf" → "checklist-fake-news-2.pdf", so a downloaded file keeps a readable name. */
function readableName(name: string): string {
  const dot = name.lastIndexOf('.');
  const base = (dot > 0 ? name.slice(0, dot) : name).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const extension = dot > 0 ? name.slice(dot + 1).toLowerCase() : 'pdf';
  return `${(base || 'bestand').slice(0, 60)}.${extension}`;
}

/** Larger files load too slowly on a phone; a game fragment only needs a few seconds. */
const MAX_UPLOAD_MB = 50;

/**
 * Image, video, audio or downloadable file (pdf, …) URL with an upload to Firebase Storage under
 * content/<page>/. A file keeps its own name and opens in the browser (its pdf viewer).
 */
@Component
export default class ImageField extends Vue {
  @Prop({ required: true }) readonly value!: string;
  @Prop({ required: true }) readonly label!: string;
  @Prop({ required: true }) readonly page!: string;
  @Prop({ type: Array, default: () => [] }) readonly errorMessages!: string[];
  @Prop({ type: String, default: 'image' }) readonly kind!: MediaKind;
  @Prop({ type: String, default: '' }) readonly hint!: string;

  uploading = false;
  mediaUrl = mediaUrl;

  get uploadLabel(): string {
    return UPLOAD_LABELS[this.kind];
  }

  get accept(): string {
    return ACCEPT[this.kind];
  }

  async upload(file: File | null): Promise<void> {
    if (!file) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      const size = Math.round(file.size / 1024 / 1024);
      const text = this.kind === 'video'
        ? `Video is te groot (${size} MB, max ${MAX_UPLOAD_MB} MB). Comprimeer de video (bv. HandBrake "Web"-preset) zodat ze vlot afspeelt op mobiel.`
        : `Bestand is groter dan ${MAX_UPLOAD_MB} MB`;
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text });
      return;
    }
    // `accept` only filters the file picker; a dropped file or "all files" in the picker skips it.
    if (this.kind === 'image' && !IMAGE_TYPES.includes(file.type)) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text: 'Kies een JPG, PNG, WebP, GIF of SVG. Een iPhone-foto (HEIC) eerst als JPG bewaren.' });
      return;
    }
    // MP4 (H.264) only: .mov/.webm do not play reliably on iOS Safari.
    if (this.kind === 'video' && file.type !== 'video/mp4') {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text: 'Kies een MP4-video (H.264).' });
      return;
    }
    this.uploading = true;
    try {
      const extension = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      if (this.kind === 'file') {
        // Own folder per upload: the file keeps its readable name; "inline" opens it in the browser, and saving it from
        // there uses that name.
        const name = readableName(file.name);
        const remotePath = `content/${this.page}/${id}/${name}`;
        await StorageServices.UploadPhoto(remotePath, file, `inline; filename="${name}"`);
        this.$emit('input', await StorageServices.GetDownloadUrl(remotePath));
        this.uploading = false;
        return;
      }
      const remotePath = `content/${this.page}/${id}.${extension}`;
      await StorageServices.UploadPhoto(remotePath, file);
      this.$emit('input', await StorageServices.GetDownloadUrl(remotePath));
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, { color: 'error', text: 'Uploaden mislukt' });
    }
    this.uploading = false;
  }
}
